// Pure sanitisation + format conversion for the DateField / TimeField
// components. Lives outside the components so unit tests don't have to
// stand up a Solid render.
//
// Why text inputs instead of <input type="date"> / type="time"?
// Chromium reads the browser's UI language for the native widgets, not
// `<html lang>` or the input's own `lang` attribute. Belgian operators
// on a default-English Chrome see mm/dd/yyyy and AM/PM regardless of
// any markup we add. Plain text inputs constrained by these helpers
// keep DD-MM-YYYY and HH:MM 24h everywhere.

export function sanitiseHHMM(raw: string): string {
  let s = raw.replace(/[^\d:]/g, "");
  if (!s.includes(":") && s.length > 2) s = s.slice(0, 2) + ":" + s.slice(2);
  return s.slice(0, 5);
}

// User typing → auto-format as DD-MM-YYYY. Strip everything but digits,
// then re-derive dashes from digit count. Means "15052026" types out
// to "15-05-2026" without the user inserting dashes manually, and
// pasting "15-05-2026" stays as-is.
export function sanitiseDate(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

// ISO YYYY-MM-DD (backend wire format) → DD-MM-YYYY (display).
// Empty string for empty / malformed input.
export function isoToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
}

// Inverse of isoToDisplay. Returns "" for partial typing, missing
// dashes, or out-of-range month/day. Backend re-validates exact dates
// (Feb 30 etc.) so we keep the client check coarse.
export function displayToIso(s: string): string {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  if (!m) return "";
  const day = parseInt(m[1]!, 10);
  const month = parseInt(m[2]!, 10);
  if (day < 1 || day > 31 || month < 1 || month > 12) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}
